// SPDX-License-Identifier: AGPL-3.0-or-later
import { z } from 'zod';
import { CodingSchema } from './codings.js';
/**
 * Zod schemas for FHIR R4 `Questionnaire`. Every object schema uses
 * `.passthrough()` so unknown FHIR fields round-trip through parse →
 * serialise unchanged. We only validate the fields intakeform actively
 * consumes.
 *
 * Reference: https://www.hl7.org/fhir/R4/questionnaire.html
 */
// --- Primitives -----------------------------------------------------------
/** FHIR `date` — YYYY, YYYY-MM, or YYYY-MM-DD. */
export const FhirDateSchema = z.string().regex(/^\d{4}(-\d{2}(-\d{2})?)?$/u, 'invalid FHIR date');
/** FHIR `dateTime` — same as date plus optional time and timezone. */
export const FhirDateTimeSchema = z
    .string()
    .regex(/^\d{4}(-\d{2}(-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?)?)?)?$/u, 'invalid FHIR dateTime');
/** FHIR `time` — HH:MM:SS[.SSS]. */
export const FhirTimeSchema = z.string().regex(/^\d{2}:\d{2}:\d{2}(\.\d+)?$/u, 'invalid FHIR time');
// --- Common shapes --------------------------------------------------------
/** FHIR `Expression`. */
export const ExpressionSchema = z
    .object({
    language: z.string(),
    expression: z.string().optional(),
    description: z.string().optional(),
    name: z.string().optional(),
    reference: z.string().optional(),
})
    .passthrough();
/** FHIR `Attachment` — used by signature + file-upload items in QR answers. */
export const AttachmentSchema = z
    .object({
    contentType: z.string().optional(),
    language: z.string().optional(),
    data: z.string().optional(),
    url: z.string().optional(),
    size: z.number().int().nonnegative().optional(),
    hash: z.string().optional(),
    title: z.string().optional(),
    creation: FhirDateTimeSchema.optional(),
})
    .passthrough();
/** FHIR `Reference`. */
export const ReferenceSchema = z
    .object({
    reference: z.string().optional(),
    type: z.string().optional(),
    display: z.string().optional(),
})
    .passthrough();
/** FHIR `Quantity` (used by enableWhen / answer). */
export const QuantitySchema = z
    .object({
    value: z.number().optional(),
    comparator: z.enum(['<', '<=', '>=', '>']).optional(),
    unit: z.string().optional(),
    system: z.string().optional(),
    code: z.string().optional(),
})
    .passthrough();
// --- Array bounds ---------------------------------------------------------
/**
 * Upper bounds on the arrays of a Questionnaire, the staff-side counterpart
 * of the MAX_RESPONSE_* caps in response.ts (GHSA-mrhx-c7xc-m3w5). zod records
 * one issue per failing array element; from zod 4.6.6 a `.max()` is checked
 * before any element is parsed, so an oversized array costs one issue
 * instead of hundreds of MiB. Only authenticated staff can create or update
 * a questionnaire, but a stolen session or a buggy importer should still not
 * be able to take the api down with one 1 MiB body.
 *
 * The 20 seeded templates peak at 9 items in one level, 13 answer options,
 * 2 extensions and 1 enableWhen / code per item. The limits sit far above
 * that, and above what long real-world forms carry:
 *   - items: 1000 per level, the same as MAX_RESPONSE_ITEMS, so every
 *     question a valid Questionnaire can hold at one level can also be
 *     answered in a valid response.
 *   - answer options: 5000. Option lists are the one place real forms run
 *     long: a country list is ~250 entries, a medication pick-list can run
 *     to a few thousand. 5000 is also about what the api's 20 000-value
 *     budget admits: 5000 plain-string options, or 3000 coded ones.
 *   - initial values: 1000, the same as MAX_RESPONSE_ANSWERS, since each
 *     initial value pre-fills one answer.
 *   - enableWhen conditions, codes, extensions, identifiers and the other
 *     string lists: 100. Real forms use a handful.
 *
 * These bound each array, not the tree: items nest, so the questionnaire
 * service also caps the total number of values before it parses at all.
 */
export const MAX_QUESTIONNAIRE_ITEMS = 1000;
export const MAX_QUESTIONNAIRE_ANSWER_OPTIONS = 5000;
export const MAX_QUESTIONNAIRE_INITIAL = 1000;
export const MAX_QUESTIONNAIRE_LIST = 100;
export const ExtensionSchema = z.lazy(() => z
    .object({
    url: z.string(),
    valueString: z.string().optional(),
    valueBoolean: z.boolean().optional(),
    valueDecimal: z.number().optional(),
    valueInteger: z.number().int().optional(),
    valueDate: FhirDateSchema.optional(),
    valueDateTime: FhirDateTimeSchema.optional(),
    valueTime: FhirTimeSchema.optional(),
    valueCode: z.string().optional(),
    valueUri: z.string().optional(),
    valueUrl: z.string().optional(),
    valueCanonical: z.string().optional(),
    valueId: z.string().optional(),
    valueMarkdown: z.string().optional(),
    valueBase64Binary: z.string().optional(),
    valueCoding: CodingSchema.optional(),
    valueQuantity: QuantitySchema.optional(),
    valueAttachment: AttachmentSchema.optional(),
    valueReference: ReferenceSchema.optional(),
    valueExpression: ExpressionSchema.optional(),
    // Bounded because a patient's QuestionnaireResponse and a staff
    // Questionnaire both carry these (GHSA-mrhx-c7xc-m3w5); the same 100 as
    // MAX_RESPONSE_EXTENSIONS in response.ts and MAX_QUESTIONNAIRE_LIST.
    extension: z.array(ExtensionSchema).max(100).optional(),
})
    .passthrough());
// --- AnswerOption ---------------------------------------------------------
export const AnswerOptionSchema = z
    .object({
    valueInteger: z.number().int().optional(),
    valueDate: FhirDateSchema.optional(),
    valueTime: FhirTimeSchema.optional(),
    valueString: z.string().optional(),
    valueCoding: CodingSchema.optional(),
    valueReference: ReferenceSchema.optional(),
    initialSelected: z.boolean().optional(),
    extension: z.array(ExtensionSchema).max(MAX_QUESTIONNAIRE_LIST).optional(),
})
    .passthrough();
// --- EnableWhen -----------------------------------------------------------
export const EnableWhenOperatorSchema = z.enum(['exists', '=', '!=', '>', '<', '>=', '<=']);
export const EnableWhenSchema = z
    .object({
    question: z.string(),
    operator: EnableWhenOperatorSchema,
    answerBoolean: z.boolean().optional(),
    answerDecimal: z.number().optional(),
    answerInteger: z.number().int().optional(),
    answerDate: FhirDateSchema.optional(),
    answerDateTime: FhirDateTimeSchema.optional(),
    answerTime: FhirTimeSchema.optional(),
    answerString: z.string().optional(),
    answerCoding: CodingSchema.optional(),
    answerQuantity: QuantitySchema.optional(),
    answerReference: ReferenceSchema.optional(),
})
    .passthrough();
// --- QuestionnaireItem (recursive) ----------------------------------------
/**
 * The full FHIR R4 `Questionnaire.item.type` value set. The nine field
 * types in intakeform's PLAN.md §3 map to a subset of these.
 */
export const QuestionnaireItemTypeSchema = z.enum([
    'group',
    'display',
    'boolean',
    'decimal',
    'integer',
    'date',
    'dateTime',
    'time',
    'string',
    'text',
    'url',
    'choice',
    'open-choice',
    'attachment',
    'reference',
    'quantity',
]);
export const QuestionnaireItemSchema = z.lazy(() => z
    .object({
    linkId: z.string(),
    definition: z.string().optional(),
    code: z.array(CodingSchema).max(MAX_QUESTIONNAIRE_LIST).optional(),
    prefix: z.string().optional(),
    text: z.string().optional(),
    type: QuestionnaireItemTypeSchema,
    enableWhen: z.array(EnableWhenSchema).max(MAX_QUESTIONNAIRE_LIST).optional(),
    enableBehavior: z.enum(['all', 'any']).optional(),
    required: z.boolean().optional(),
    repeats: z.boolean().optional(),
    readOnly: z.boolean().optional(),
    maxLength: z.number().int().nonnegative().optional(),
    answerValueSet: z.string().optional(),
    answerOption: z.array(AnswerOptionSchema).max(MAX_QUESTIONNAIRE_ANSWER_OPTIONS).optional(),
    initial: z.array(AnswerOptionSchema).max(MAX_QUESTIONNAIRE_INITIAL).optional(),
    item: z.array(QuestionnaireItemSchema).max(MAX_QUESTIONNAIRE_ITEMS).optional(),
    extension: z.array(ExtensionSchema).max(MAX_QUESTIONNAIRE_LIST).optional(),
    modifierExtension: z.array(ExtensionSchema).max(MAX_QUESTIONNAIRE_LIST).optional(),
})
    .passthrough());
// --- Questionnaire (top level) --------------------------------------------
export const PublicationStatusSchema = z.enum(['draft', 'active', 'retired', 'unknown']);
export const QuestionnaireSchema = z
    .object({
    resourceType: z.literal('Questionnaire'),
    id: z.string().optional(),
    url: z.string().optional(),
    identifier: z.array(z.unknown()).max(MAX_QUESTIONNAIRE_LIST).optional(),
    version: z.string().optional(),
    name: z.string().optional(),
    title: z.string().optional(),
    derivedFrom: z.array(z.string()).max(MAX_QUESTIONNAIRE_LIST).optional(),
    status: PublicationStatusSchema,
    experimental: z.boolean().optional(),
    subjectType: z.array(z.string()).max(MAX_QUESTIONNAIRE_LIST).optional(),
    date: FhirDateTimeSchema.optional(),
    publisher: z.string().optional(),
    description: z.string().optional(),
    purpose: z.string().optional(),
    copyright: z.string().optional(),
    approvalDate: FhirDateSchema.optional(),
    lastReviewDate: FhirDateSchema.optional(),
    code: z.array(CodingSchema).max(MAX_QUESTIONNAIRE_LIST).optional(),
    item: z.array(QuestionnaireItemSchema).max(MAX_QUESTIONNAIRE_ITEMS).optional(),
    extension: z.array(ExtensionSchema).max(MAX_QUESTIONNAIRE_LIST).optional(),
    modifierExtension: z.array(ExtensionSchema).max(MAX_QUESTIONNAIRE_LIST).optional(),
})
    .passthrough();
//# sourceMappingURL=questionnaire.js.map